/**
 * @file EntityFormErrorState.tsx
 * @description Unified error / empty state for entity form resolution failures.
 * Replaces inconsistent error handling across legacy and workspace form pages.
 */

import { AlertCircle, FileWarning } from 'lucide-react';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import type { EntityFormType } from '../../types/entityFormResolution';

interface EntityFormErrorStateProps {
  /** The entity type that failed to resolve */
  entityType: EntityFormType;
  /** Error message from the API or resolution hook */
  errorMessage?: string;
  /** True when resolution succeeded but no form was found */
  noFormAvailable?: boolean;
}

const ENTITY_LABELS: Record<EntityFormType, string> = {
  HOSPITAL: 'Hospital',
  HCP: 'HCP',
  PATIENT: 'Patient',
  PHARMA: 'Pharmacy',
  CASE: 'Case',
  UTILIZATION: 'Utilization',
  CLAIM: 'Claim',
};

/**
 * EntityFormErrorState provides a user-friendly error or empty state
 * when form resolution fails. Shows actionable guidance.
 */
export function EntityFormErrorState({
  entityType,
  errorMessage,
  noFormAvailable = false,
}: EntityFormErrorStateProps) {
  const label = ENTITY_LABELS[entityType] ?? entityType;

  if (noFormAvailable) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="rounded-full bg-muted p-3 mb-4">
          <AppIcon Icon={FileWarning} variant="warning" size="default" />
        </div>
        <h3 className="text-sm font-semibold text-foreground mb-1">
          {label} form not configured
        </h3>
        <p className="text-xs text-muted-foreground max-w-md">
          No published form is available for {label} in this program.
          Please create and publish a {label} form in the Form Builder,
          then map it to the workflow.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="rounded-full bg-destructive/10 p-3 mb-4">
        <AppIcon Icon={AlertCircle} variant="error" size="default" />
      </div>
      <h3 className="text-sm font-semibold text-foreground mb-1">
        Failed to load {label} form
      </h3>
      <p className="text-xs text-muted-foreground max-w-md">
        {errorMessage || `The ${label} form could not be loaded. Please ensure a ${label} form is published and mapped to the workflow step.`}
      </p>
    </div>
  );
}
